import React, { useMemo, useState } from "react";
import { useFormikContext } from "formik";
import { Button } from "../../components/ui";
import { useEntitlement } from "../../contexts";
import { STATE_TONE, checkedTooFew, getPriceCheck } from "./priceCheck";
import { listingCurrency, verdictCopy } from "./priceCheckCopy";
import useAccountChecks from "./useAccountChecks";
import PriceCheckPanel, { MarketplaceLogoButton } from "./PriceCheckPanel";
import "./price-check.scss";

// Only a verdict earns the bar. A new listing, one the nightly check has not
// reached, or one with nothing to compare against shows nothing at all.
const SHOWN = ["above", "below", "in_range", "reviewed"];
// …plus a dated check that found too few priced listings (0 or 1) to compare.

const blankToNull = (v) => (v === "" || v === undefined ? null : v);

// The unsaved prices of every channel the panel can show, in major units.
const livePricesFrom = (values) => {
  const accounts = {};
  Object.entries(values?.marketplace_states || {}).forEach(([k, fields]) => {
    const m = /^acc(\d+)$/.exec(k);
    if (m) accounts[m[1]] = blankToNull(fields?.price);
  });
  return {
    chrono: blankToNull(values?.chrono_price),
    ebay: blankToNull(values?.ebay_price),
    wf: blankToNull(values?.wf_price),
    accounts,
  };
};

/**
 * Price check bar at the top of a sales-channel tab of the listing form —
 * CH24 UK ("chrono"), every other Chrono24 account ("chrono-{id}", judged from
 * the same check converted into its currency), eBay and WatchFinder.
 * Judges the LIVE (unsaved) form price, so the verdict moves as the rep types.
 * "Review Details" opens the same Price check popup as the listing cards; a
 * price set there only fills this form's field — the form's own auto-save
 * persists it, as for any other edit.
 */
export default function PriceCheckTabBar({ listing, tabKey }) {
  const entitled = useEntitlement("competitive_pricing_check");
  const { values, setFieldValue } = useFormikContext();
  const [open, setOpen] = useState(false);
  const isAccount = String(tabKey).startsWith("chrono-");

  const livePrices = useMemo(() => livePricesFrom(values), [values]);
  const { checks: accountChecks } = useAccountChecks(listing, {
    livePrices: livePrices.accounts,
    enabled: entitled && isAccount && !!listing?.id,
  });

  if (!entitled || !listing?.id) return null;
  const accountCheck = isAccount ? accountChecks.find((a) => a.key === tabKey) : null;
  if (isAccount && !accountCheck) return null;
  const check = accountCheck ? accountCheck.check : getPriceCheck(listing, tabKey, { priceOverride: values?.[`${tabKey}_price`] });
  if (!SHOWN.includes(check.state) && !checkedTooFew(check)) return null;

  const currency = accountCheck ? check.currency : listingCurrency(listing);
  const label = accountCheck ? accountCheck.account.label : check.label;
  const { title, body } = verdictCopy({ ...check, label }, currency);

  const onSetPrice = (key, value, accountId) => {
    setFieldValue(accountId ? `marketplace_states.acc${accountId}.price` : `${key}_price`, value);
  };

  return (
    <div className="pc-editbar" data-testid={`price-check-bar-${tabKey}`}>
      <div className={`pc-editbar__bar pc-tone--${STATE_TONE[check.state]}`} role="status">
        <span className="pc-editbar__eyebrow">Price check</span>
        <span className="pc-editbar__title">{title}</span>
        <span className="pc-editbar__body">{body}</span>
        <span className="pc-editbar__actions">
          <Button size="md" variant="primary" onClick={() => setOpen(true)}>
            Review Details
          </Button>
          <MarketplaceLogoButton check={check} />
        </span>
      </div>

      <PriceCheckPanel
        open={open}
        onClose={() => setOpen(false)}
        listing={listing}
        marketplace={tabKey}
        livePrices={livePrices}
        onSetPrice={onSetPrice}
      />
    </div>
  );
}
